import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AlertTriangle, Heart, ImageOff, LayoutGrid, Loader2, Rows3, Sparkles, Trash2, Wand2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useSignedImage } from "@/hooks/useSignedImage";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import StickyScroll from "@/components/ui/sticky-scroll";
import { FlipReveal, FlipRevealItem } from "@/components/ui/flip-reveal";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { SEO } from "@/components/SEO";
import { MobileShell } from "@/components/mobile";
import { useIsMobile } from "@/hooks/use-mobile";
import { AdSlot } from "@/components/ads/AdSlot";
import { ADSENSE_SLOT_BANNER } from "@/lib/should-show-ads";
import { ExpiryBanner } from "@/components/ExpiryBanner";

interface Row {
  id: string;
  generated_image_url: string | null;
  user_photo_url: string;
  fit_score: number | null;
  suits: boolean | null;
  product_category: string | null;
  is_favorite: boolean;
  created_at: string;
  kind: string;
  aura_score: number | null;
  status: string;
  image_expires_at: string | null;
}

type ViewMode = "grid" | "stack";
type FilterMode = "all" | "favorites" | "aura";

/** Small heart toggle used inside both tile types. */
const FavoriteButton = ({
  active,
  onClick,
}: {
  active: boolean;
  onClick: (e: React.MouseEvent) => void;
}) => (
  <button
    type="button"
    onClick={(e) => {
      e.stopPropagation();
      e.preventDefault();
      onClick(e);
    }}
    aria-label={active ? "Remove from favorites" : "Add to favorites"}
    className={cn(
      "absolute right-2 top-2 z-10 flex h-8 w-8 items-center justify-center rounded-full backdrop-blur transition-colors",
      active
        ? "bg-primary/90 text-primary-foreground hover:bg-primary"
        : "bg-black/45 text-white hover:bg-black/65",
    )}
  >
    <Heart className={cn("h-4 w-4", active && "fill-current")} />
  </button>
);

/**
 * Tile for a standard try-on entry. Resolves its own signed URL.
 */
const TryOnTile = ({
  row,
  onDelete,
  onToggleFavorite,
}: {
  row: Row;
  onDelete: (id: string) => void;
  onToggleFavorite: (id: string, next: boolean) => void;
}) => {
  const navigate = useNavigate();
  const img = useSignedImage("tryon-results", row.generated_image_url);
  const fallback = useSignedImage("user-photos", row.user_photo_url);
  const src = img ?? fallback;
  const processing = row.status === "processing";

  return (
    <div className="group relative aspect-[3/4] overflow-hidden rounded-2xl bg-muted shadow-soft ring-1 ring-white/5">
      <button
        type="button"
        onClick={() => !processing && navigate(`/app/result/${row.id}`)}
        disabled={processing}
        className="block h-full w-full text-left transition-transform active:scale-[0.98] disabled:cursor-default"
      >
        {src ? (
          <img
            src={src}
            alt=""
            loading="lazy"
            className={cn(
              "h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]",
              processing && "opacity-40 blur-[2px]",
            )}
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-muted-foreground">
            <ImageOff className="h-6 w-6" />
          </div>
        )}
        {processing && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-background/40 backdrop-blur-sm">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
            <span className="rounded-full border border-primary/40 bg-background/85 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-primary backdrop-blur-md">
              Generating…
            </span>
          </div>
        )}
        {!processing && (
          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-2">
            <div className="flex items-center justify-between text-xs text-white">
              <span className="capitalize font-medium">{row.product_category ?? "look"}</span>
              {typeof row.fit_score === "number" && (
                <span className="rounded-full bg-white/20 px-2 py-0.5 backdrop-blur tabular-nums">
                  {row.fit_score}/10
                </span>
              )}
            </div>
          </div>
        )}
        {row.suits === false && !processing && (
          <span className="absolute left-2 top-2 rounded-full bg-warning px-2 py-0.5 text-[10px] font-semibold text-warning-foreground">
            ⚠
          </span>
        )}
        {!processing && (
          <ExpiryBanner
            compact
            expiresAt={row.image_expires_at}
            hasImage={!!row.generated_image_url}
            className="absolute left-1/2 top-2 -translate-x-1/2"
          />
        )}
      </button>
      <FavoriteButton
        active={row.is_favorite}
        onClick={() => onToggleFavorite(row.id, !row.is_favorite)}
      />
      <button
        type="button"
        onClick={() => onDelete(row.id)}
        aria-label="Delete try-on"
        className="absolute left-2 bottom-2 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-black/55 text-white backdrop-blur transition-colors hover:bg-destructive"
      >
        <Trash2 className="h-3.5 w-3.5" />
      </button>
    </div>
  );
};

/**
 * Tile for a standalone Aura entry. Visually distinct + jumps to AuraView.
 */
const AuraTile = ({
  row,
  onDelete,
  onToggleFavorite,
}: {
  row: Row;
  onDelete: (id: string) => void;
  onToggleFavorite: (id: string, next: boolean) => void;
}) => {
  const navigate = useNavigate();
  const src = useSignedImage("user-photos", row.user_photo_url);

  return (
    <div className="group relative aspect-[3/4] overflow-hidden rounded-2xl bg-muted shadow-soft ring-1 ring-primary/30">
      <button
        type="button"
        onClick={() => navigate(`/app/aura/${row.id}`)}
        className="block h-full w-full text-left transition-transform active:scale-[0.98]"
      >
        {src ? (
          <img
            src={src}
            alt=""
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-muted-foreground">
            <ImageOff className="h-6 w-6" />
          </div>
        )}

        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-primary/40 via-primary/5 to-transparent mix-blend-overlay" />

        <span className="absolute left-2 top-2 flex items-center gap-1 rounded-full bg-background/85 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-primary backdrop-blur-md">
          <Wand2 className="h-3 w-3" />
          Aura
        </span>

        {typeof row.aura_score === "number" && (
          <span className="absolute right-12 top-2 rounded-full border border-primary/40 bg-background/85 px-2 py-0.5 text-[11px] font-bold tabular-nums text-foreground backdrop-blur-md">
            {row.aura_score}
          </span>
        )}

        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-2">
          <div className="flex items-center justify-between text-xs text-white">
            <span className="font-medium">Aura read</span>
            <span className="text-white/80">/100</span>
          </div>
        </div>
      </button>
      <FavoriteButton
        active={row.is_favorite}
        onClick={() => onToggleFavorite(row.id, !row.is_favorite)}
      />
      <button
        type="button"
        onClick={() => onDelete(row.id)}
        aria-label="Delete aura"
        className="absolute left-2 bottom-2 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-black/55 text-white backdrop-blur transition-colors hover:bg-destructive"
      >
        <Trash2 className="h-3.5 w-3.5" />
      </button>
    </div>
  );
};

/** Segmented Grid ↔ Stack toggle, shared between hero + grid header. */
const ViewToggle = ({
  view,
  setView,
  className,
}: {
  view: ViewMode;
  setView: (v: ViewMode) => void;
  className?: string;
}) => (
  <div
    className={cn(
      "inline-flex items-center rounded-full border border-white/15 bg-white/[0.03] p-0.5",
      className,
    )}
  >
    {(
      [
        { k: "grid", icon: LayoutGrid, label: "Grid" },
        { k: "stack", icon: Rows3, label: "Stack" },
      ] as const
    ).map(({ k, icon: Icon, label }) => (
      <button
        key={k}
        type="button"
        onClick={() => setView(k)}
        className={cn(
          "flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.2em] transition-colors",
          view === k
            ? "bg-primary text-primary-foreground shadow-[0_0_20px_-6px_hsl(var(--primary))]"
            : "text-muted-foreground hover:text-foreground",
        )}
        aria-pressed={view === k}
      >
        <Icon className="h-3.5 w-3.5" />
        {label}
      </button>
    ))}
  </div>
);

const FilterChips = ({
  filter,
  setFilter,
}: {
  filter: FilterMode;
  setFilter: (k: FilterMode) => void;
}) => (
  <div className="flex flex-wrap gap-2">
    {(["all", "favorites", "aura"] as const).map((k) => (
      <button
        key={k}
        onClick={() => setFilter(k)}
        className={cn(
          "font-display rounded-full border px-4 py-1.5 text-[10px] font-semibold uppercase tracking-[0.2em] transition-colors",
          filter === k
            ? "border-primary bg-primary text-primary-foreground shadow-[0_0_20px_-6px_hsl(var(--primary))]"
            : "border-white/15 bg-white/[0.03] text-muted-foreground hover:border-primary/50 hover:text-foreground",
        )}
      >
        {k === "all" ? "All" : k === "favorites" ? "Favorites" : "Aura"}
      </button>
    ))}
  </div>
);

const Hero = ({
  filter,
  setFilter,
  count,
  view,
  setView,
}: {
  filter: FilterMode;
  setFilter: (k: FilterMode) => void;
  count: number;
  view: ViewMode;
  setView: (v: ViewMode) => void;
}) => (
  <div className="px-1 pt-2 pb-4 md:py-0">
    <span className="font-display hidden text-[10px] font-semibold uppercase tracking-[0.3em] text-primary md:inline">
      — HISTORY
    </span>
    <h1 className="font-display text-3xl font-bold uppercase leading-[0.95] tracking-tight md:mt-2 md:text-5xl">
      My Looks
    </h1>
    <p className="mt-3 max-w-sm text-sm text-muted-foreground md:text-base">
      Try-ons and aura reads, scrolling past in stacked sticky columns.
      <span className="ml-1 text-foreground/80">{count} saved.</span>
    </p>

    <div className="mt-5 flex flex-wrap items-center gap-3">
      <FilterChips filter={filter} setFilter={setFilter} />
      <ViewToggle view={view} setView={setView} />
    </div>

    <p className="mt-5 flex items-center gap-1.5 text-xs text-muted-foreground">
      <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
      AI-generated — may contain inaccuracies.
    </p>
    <p className="mt-2 flex items-start gap-1.5 text-[11px] leading-snug text-warning">
      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
      Cloud images auto-delete after 24 hours — download anything you want to keep.
    </p>

    <p className="mt-6 hidden text-[10px] font-semibold uppercase tracking-[0.3em] text-muted-foreground md:block">
      Scroll down 👇
    </p>
  </div>
);

const Footer = () => (
  <div className="mx-auto w-full max-w-md rounded-3xl border border-primary/30 bg-gradient-to-br from-primary/10 to-transparent p-8 text-center shadow-elevated">
    <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl gradient-primary text-primary-foreground shadow-glow">
      <Sparkles className="h-6 w-6" />
    </div>
    <h2 className="font-display mt-4 text-xl font-bold uppercase tracking-tight">Make another</h2>
    <p className="mt-1 text-sm text-muted-foreground">
      Drop a new product or read your aura — fresh looks land here instantly.
    </p>
    <div className="mt-5 grid grid-cols-2 gap-2">
      <Button asChild variant="outline">
        <Link to="/app">New try-on</Link>
      </Button>
      <Button asChild>
        <Link to="/app/aura">
          <Wand2 className="mr-1.5 h-4 w-4" />
          Read aura
        </Link>
      </Button>
    </div>
  </div>
);

const VIEW_STORAGE_KEY = "history.view";

const History = () => {
  const isMobile = useIsMobile();
  const { user } = useAuth();
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<FilterMode>("all");
  const [view, setViewState] = useState<ViewMode>(() => {
    if (typeof window === "undefined") return "stack";
    const stored = window.localStorage.getItem(VIEW_STORAGE_KEY);
    return stored === "grid" || stored === "stack" ? stored : "stack";
  });
  const [pendingDelete, setPendingDelete] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const setView = (v: ViewMode) => {
    setViewState(v);
    try {
      window.localStorage.setItem(VIEW_STORAGE_KEY, v);
    } catch {
      // ignore storage errors (private mode etc.)
    }
  };

  // SEO handled by <SEO /> component in render.

  useEffect(() => {
    if (!user) return;
    let active = true;
    setLoading(true);
    const q = supabase
      .from("tryons")
      .select("id,generated_image_url,user_photo_url,fit_score,suits,product_category,is_favorite,created_at,kind,aura_score,status,image_expires_at")
      .eq("user_id", user.id)
      // Hide failed rows but keep processing ones visible so users can see
      // an in-flight generation if they navigated away mid-scan.
      .neq("status", "failed")
      .order("created_at", { ascending: false })
      .limit(60);
    q.then(({ data }) => {
      if (!active) return;
      setRows((data ?? []) as Row[]);
      setLoading(false);
    });
    return () => { active = false; };
  }, [user]);

  /** Filter key for FlipReveal: "all" shows everything; otherwise key = current filter. */
  const flipKeys = filter === "all" ? ["all"] : [filter];

  const handleConfirmDelete = async () => {
    if (!pendingDelete || !user) return;
    setDeleting(true);
    const id = pendingDelete;
    const row = rows.find((r) => r.id === id);
    if (row?.generated_image_url) {
      await supabase.storage.from("tryon-results").remove([row.generated_image_url]);
    }
    if (row?.user_photo_url) {
      await supabase.storage.from("user-photos").remove([row.user_photo_url]);
    }
    const { error } = await supabase.from("tryons").delete().eq("id", id).eq("user_id", user.id);
    setDeleting(false);
    setPendingDelete(null);
    if (error) {
      toast.error("Couldn't delete. Try again.");
      return;
    }
    setRows((prev) => prev.filter((r) => r.id !== id));
    toast.success("Deleted");
  };

  /** Optimistic favorite toggle. Reverts on error. */
  const handleToggleFavorite = async (id: string, next: boolean) => {
    if (!user) return;
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, is_favorite: next } : r)));
    const { error } = await supabase
      .from("tryons")
      .update({ is_favorite: next })
      .eq("id", id)
      .eq("user_id", user.id);
    if (error) {
      setRows((prev) => prev.map((r) => (r.id === id ? { ...r, is_favorite: !next } : r)));
      toast.error("Couldn't update favorite");
      return;
    }
    // Filtering is now client-side — no need to drop the row from local state.
  };

  /**
   * Per-row flip key — encodes both kind and favorite state so a single row
   * can match multiple filters (e.g. a favorited aura matches both "favorites"
   * and "aura"). FlipReveal only supports one key per item, so we expand the
   * filter's `keys` list to cover every bucket that should be visible.
   */
  const rowKey = (r: Row): string => {
    const base = r.kind === "aura" ? "aura" : "tryon";
    return r.is_favorite ? `fav-${base}` : base;
  };

  /** Map current filter to the set of row keys that should be visible. */
  const flipShowKeys =
    filter === "all"
      ? ["all"]
      : filter === "favorites"
        ? ["fav-tryon", "fav-aura"]
        : ["aura", "fav-aura"];

  /** Rows visible for the current filter — used by both stack and grid views. */
  const visibleRows = rows.filter(
    (r) => flipShowKeys.includes("all") || flipShowKeys.includes(rowKey(r)),
  );

  const visibleCount = visibleRows.length;

  // Build the gallery tiles. In grid view we wrap with FlipRevealItem so the
  // FlipReveal parent can animate show/hide. In stack view we filter upfront
  // because StickyScroll distributes tiles round-robin and isn't a FlipReveal.
  const gridTiles = rows.map((r) => (
    <FlipRevealItem key={r.id} flipKey={rowKey(r)}>
      {r.kind === "aura" ? (
        <AuraTile row={r} onDelete={setPendingDelete} onToggleFavorite={handleToggleFavorite} />
      ) : (
        <TryOnTile row={r} onDelete={setPendingDelete} onToggleFavorite={handleToggleFavorite} />
      )}
    </FlipRevealItem>
  ));

  const stackTiles = visibleRows.map((r) =>
    r.kind === "aura" ? (
      <AuraTile key={r.id} row={r} onDelete={setPendingDelete} onToggleFavorite={handleToggleFavorite} />
    ) : (
      <TryOnTile key={r.id} row={r} onDelete={setPendingDelete} onToggleFavorite={handleToggleFavorite} />
    ),
  );

  const hero = (
    <Hero filter={filter} setFilter={setFilter} count={visibleCount} view={view} setView={setView} />
  );

  const renderContent = () => {
    if (loading) {
      return view === "stack" ? (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 md:gap-10">
          <div className="h-[60vh] animate-pulse rounded-3xl bg-muted" />
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="aspect-[3/4] animate-pulse rounded-2xl bg-muted" />
            ))}
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="aspect-[3/4] animate-pulse rounded-2xl bg-muted" />
          ))}
        </div>
      );
    }

    if (rows.length === 0) {
      return (
        <div className="mt-12 flex flex-col items-center text-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-2xl gradient-primary text-primary-foreground shadow-glow">
            <Sparkles className="h-7 w-7" />
          </div>
          <h2 className="mt-4 text-lg font-bold">Nothing here yet</h2>
          <p className="mt-1 max-w-xs text-sm text-muted-foreground">
            {filter === "aura"
              ? "Read your aura to see it here."
              : "Generate your first look to start building a wardrobe."}
          </p>
          <Button asChild className="mt-5">
            <Link to={filter === "aura" ? "/app/aura" : "/app"}>
              {filter === "aura" ? "Read your aura" : "Try something on"}
            </Link>
          </Button>
        </div>
      );
    }

    if (view === "stack") {
      return <StickyScroll hero={hero} tiles={stackTiles} footer={<Footer />} columns={3} />;
    }

    // GRID view — flat header + responsive grid.
    return (
      <div>
        <div className="mb-5 flex flex-col gap-4">
          <div>
            <span className="font-display text-[10px] font-semibold uppercase tracking-[0.3em] text-primary">
              — HISTORY
            </span>
            <h1 className="font-display mt-2 text-3xl font-bold uppercase leading-[0.95] tracking-tight md:text-4xl">
              My Looks
            </h1>
            <p className="mt-2 text-sm text-muted-foreground">
              {visibleCount} saved · pick a tile to revisit it.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <FilterChips filter={filter} setFilter={setFilter} />
            <ViewToggle view={view} setView={setView} />
          </div>
          <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
            AI-generated — may contain inaccuracies.
          </p>
        </div>
        <FlipReveal
          keys={flipShowKeys}
          showClass="block"
          hideClass="hidden"
          className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4"
        >
          {gridTiles}
        </FlipReveal>
        <AdSlot slot={ADSENSE_SLOT_BANNER} format="auto" className="mt-8" />
        <div className="mt-10">
          <Footer />
        </div>
      </div>
    );
  };

  const content = (
    <>
      {renderContent()}

      <AlertDialog open={!!pendingDelete} onOpenChange={(open) => !open && setPendingDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this entry?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove it from your history. Credits won't be refunded.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); handleConfirmDelete(); }}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? "Deleting…" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );

  return (
    <>
      <SEO title="Your Try-On History – FitAura AI" description="All your past virtual try-ons in one place." noindex />
      {isMobile ? (
        <MobileShell title="History">
          <div className="mx-auto w-full max-w-6xl pt-2 pb-12">{content}</div>
        </MobileShell>
      ) : (
        <div className="mx-auto w-full max-w-6xl px-5 pt-6 pb-12">{content}</div>
      )}
    </>
  );
};

export default History;
